import React, { useRef, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight } from '@fortawesome/pro-regular-svg-icons';
import './HorizontalScrollRow.scss';

const HorizontalScrollRow = ({ children, className, contained, controlsAside, hideControls, onNearEnd, nearEndThreshold = 200 }) => {
  const containerRef = useRef(null);
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [insetLeft, setInsetLeft] = useState(0);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);

    if (onNearEnd && el.scrollWidth - (el.scrollLeft + el.clientWidth) < nearEndThreshold) {
      onNearEnd();
    }
  }, [onNearEnd, nearEndThreshold]);

  // Measure the container's left offset so the full-bleed track can extend to the viewport edge
  const updateInset = useCallback(() => {
    if (contained) return;
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setInsetLeft(rect.left);
  }, [contained]);

  useEffect(() => {
    if (contained) return;
    updateInset();
    window.addEventListener('resize', updateInset);
    return () => window.removeEventListener('resize', updateInset);
  }, [updateInset, contained]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    // Delay initial check so children have time to render and establish scrollWidth
    const frameId = requestAnimationFrame(() => {
      updateScrollState();
    });

    let rafId;
    const deferredUpdate = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateScrollState);
    };

    el.addEventListener('scroll', updateScrollState, { passive: true });
    const observer = new ResizeObserver(deferredUpdate);
    observer.observe(el);

    // Also observe children changes (e.g. async-loaded content)
    const mutationObserver = new MutationObserver(deferredUpdate);
    mutationObserver.observe(el, { childList: true, subtree: true });

    return () => {
      cancelAnimationFrame(frameId);
      cancelAnimationFrame(rafId);
      el.removeEventListener('scroll', updateScrollState);
      observer.disconnect();
      mutationObserver.disconnect();
    };
  }, [updateScrollState]);

  const scroll = direction => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.7;
    el.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
  };

  return (
    <div className={cn('horizontal-scroll-row', className)} ref={containerRef}>
      <div className='horizontal-scroll-track' ref={scrollRef} style={contained ? undefined : { paddingLeft: insetLeft, marginLeft: -insetLeft, width: `100vw` }}>
        {children}
      </div>
      {(!hideControls || controlsAside) && (
        <div className={cn('horizontal-scroll-controls-row', { 'with-aside': !!controlsAside })}>
          {controlsAside && <div className='horizontal-scroll-controls-aside'>{controlsAside}</div>}
          {!hideControls && (
            <div className='horizontal-scroll-controls'>
              <button className='scroll-arrow-btn' disabled={!canScrollLeft} onClick={() => scroll('left')}>
                <FontAwesomeIcon icon={faArrowLeft} />
              </button>
              <button className='scroll-arrow-btn' disabled={!canScrollRight} onClick={() => scroll('right')}>
                <FontAwesomeIcon icon={faArrowRight} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

HorizontalScrollRow.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  contained: PropTypes.bool,
  controlsAside: PropTypes.node,
  hideControls: PropTypes.bool,
  onNearEnd: PropTypes.func,
  nearEndThreshold: PropTypes.number,
};

export default HorizontalScrollRow;
